import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, InputNumber, Message, Panel, Tooltip, Whisper } from 'rsuite';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  useCloseDates,
  useCommitmentHistory,
  useEmployeePato,
  useEmployeePatoReport,
  useTimeOffRequests,
  useCreateTimeOffRequest,
  useRecordPlanSubmission,
  useTimesheetValidator,
  useVacationPlans,
  useSaveVacationPlan,
} from '../hooks/useQueries';
import { showToast } from '../utils/toast';
import PageHeader from '../components/shared/PageHeader';
import TimeOffRangeCalendar from '../components/timeoff/TimeOffRangeCalendar';
import VacationPlannerNotice from '../components/timeoff/VacationPlannerNotice';
import { PAYROLL_ENABLED } from '../config/payrollFeature';
import { STANDARD_WORK_DAY_HOURS, getCurrentPayPeriod, toDateOnlyString, toStartOfDay } from '../utils/payPeriods';
import { closeDateKeySet, formatDateOnly, isSameDay, parseLocalDateOnly } from '../utils/timeOffDates';
import {
  formatHours,
  formatSavedAt,
  formatShortDate,
  formatTripRange,
  hrs,
  plural,
} from '../utils/vacationPlanFormat';
import {
  buildVacationPlan,
  commitmentForPeriod,
  groupDaysIntoRequests,
  isPatoRequest,
  payPeriodFor,
  plannerAllocationForRequest,
  requestHoursByDay,
} from '../utils/vacationPlan';
import { buildPatoOutlook } from '../utils/patoOutlook';
import './TimeOffPage.css';

/*
  Vacation Planner.

  A wizard for working out how to take a stretch of time off: how much of it
  the employee's reduced commitment already covers (requested as "Entered",
  no review, unpaid) and how much has to come out of PATO (requested for
  review, paid). The arithmetic is in utils/vacationPlan.js; this page gathers
  what it needs and walks through the answer.

    1. Dates    the same calendar Create Request opens with
    2. Details  standard workday -> days and close dates -> total time off
    3. Plan     reduction -> extra hours -> PATO needed -> the entries to make

  Details and Plan ask one thing at a time: an answered question folds to a
  line, and the next is worked out behind a short loading card, then fades in.

  Advisory only for now: nothing is submitted from here.
*/

const STEPS = ['Dates', 'Details', 'Plan', 'Submit'];
const MAX_HOURS_PER_DAY = 24;
// The divisor for a non-exempt recent daily average - see ViewHoursPage.
const WEEK_WORKING_DAYS = 5;

// Moving forward shows a short working card first, so the next answer reads as
// worked out rather than appearing out of nowhere. The messages step through
// while it is up; the last one holds. Keyed `${scope}:${stage}`.
const REVEAL_MS = 1600;
const REVEAL_MESSAGE_MS = 600;
const REVEAL_MESSAGES = {
  'step:1': ['Loading your schedule…', 'Pulling your commitment…', 'Checking your existing time off…'],
  'details:1': ['Counting your working days…', 'Pulling company close dates…', 'Checking what each close date pays…'],
  'details:2': ['Adding up your time off…', 'Calculating your total…'],
  'step:2': [
    'Pulling your pay periods…',
    'Working out your reduction hours…',
    'Looking up time already entered…',
    'Checking your PATO balance…',
  ],
  'plan:1': ['Applying your reduction…', 'Working out PATO to cover…'],
  'plan:2': ['Adding in your extra hours…', 'Applying unpaid time…', 'Calculating PATO needed…'],
  'plan:3': ['Splitting your days into entries…', 'Building your plan…'],
  'step:3': ['Preparing your requests…', 'Lining up your dates…'],
};

const formatRun = (run) => (run.start === run.end
  ? formatShortDate(run.start)
  : `${formatShortDate(run.start)} to ${formatShortDate(run.end)}`);

// JSON with object keys sorted, so two sets of answers compare equal however
// their keys were ordered - Postgres jsonb hands them back in its own order.
const stableStringify = (value) => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
};

// Held as typed: coercing per keystroke would delete a trailing decimal point
// (see TimeOffCreatePage). Tidied on blur.
const typedNumber = (value) => (value === '' || value == null ? '' : value);
const tidyNumber = (value) => {
  if (value === '' || value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// The Details day list fades in a row at a time. Past the cap the rest arrive
// together, so a long trip is not a long wait.
const DAYLIST_STAGGER_MS = 45;
const DAYLIST_STAGGER_CAP = 20;

// What a day of the trip counts for, and why, in words.
const describeDay = (day, standardDay) => {
  if (day.skip === 'weekend') {
    return { kind: 'off', title: 'Weekend', detail: 'Not a working day' };
  }
  if (day.skip === 'requested') {
    return { kind: 'off', title: 'Already requested', detail: 'Covered by one of your existing requests' };
  }
  if (day.isClose) {
    return {
      kind: 'close',
      title: day.closeName,
      detail: day.hours > 0
        ? `Close date pays ${hrs(day.closeCredit)} of your ${hrs(standardDay)} day, leaving ${hrs(day.hours)} to cover`
        : `Close date pays ${hrs(day.closeCredit)}, which covers your ${hrs(standardDay)} day`,
    };
  }
  return { kind: 'work', title: 'Working day', detail: `Your standard workday: ${hrs(standardDay)}` };
};

// Stands in for the next question while it is being worked out.
const WorkingCard = ({ message, cardRef }) => (
  <div
    ref={cardRef}
    className="timeoff-planner-card timeoff-planner-card--working timeoff-planner-reveal"
    role="status"
    aria-live="polite"
  >
    <span className="timeoff-planner-spinner" aria-hidden="true" />
    <span key={message} className="timeoff-planner-working__message">{message}</span>
  </div>
);

// An answered question, folded down so the open one has the room. `ahead`
// marks one past the open question - reached before, kept on screen, and
// opened rather than changed.
const ConfirmedLine = ({ label, value, onChange, ahead = false, disabled = false }) => (
  <div className={`timeoff-planner-done timeoff-planner-reveal${ahead ? ' timeoff-planner-done--ahead' : ''}`}>
    <i className={`bi ${ahead ? 'bi-circle' : 'bi-check-circle-fill'} timeoff-planner-done__icon`} aria-hidden="true" />
    <span className="timeoff-planner-done__label">{label}</span>
    <span className="timeoff-planner-done__value">{value}</span>
    <button type="button" className="bdc-linkbtn" disabled={disabled} onClick={onChange}>
      {ahead ? 'Open' : 'Change'}
    </button>
  </div>
);

const formatTileParts = (date) => ({
  month: date.toLocaleDateString('en-US', { month: 'short' }),
  day: date.getDate(),
  weekday: date.toLocaleDateString('en-US', { weekday: 'long' }),
  year: date.getFullYear(),
});

// One date as a little calendar page.
const DateTile = ({ date, label }) => {
  const parts = formatTileParts(date);
  return (
    <div className="timeoff-planner-datetile">
      <div className="timeoff-planner-datetile__label">{label}</div>
      <div className="timeoff-planner-datetile__page">
        <div className="timeoff-planner-datetile__month">{parts.month} {parts.year}</div>
        <div className="timeoff-planner-datetile__day bdc-num">{parts.day}</div>
        <div className="timeoff-planner-datetile__weekday">{parts.weekday}</div>
      </div>
    </div>
  );
};

// The trip's dates at the top of Details and Plan. Read only: dates are
// changed on the calendar, which Back on the first question returns to.
const DatesSummary = ({ start, end, totalDays, workingDays, hoursPerDay }) => {
  const single = isSameDay(start, end);
  const facts = [
    plural(totalDays, 'day'),
    plural(workingDays, 'working day'),
    hoursPerDay != null ? `${hrs(hoursPerDay)} a day` : null,
  ].filter(Boolean);
  return (
    <div className="timeoff-planner-dates" aria-label="Your dates">
      <div className="timeoff-planner-dates__tiles">
        {single ? (
          <DateTile date={start} label="Your day off" />
        ) : (
          <>
            <DateTile date={start} label="First day off" />
            <i className="bi bi-arrow-right timeoff-planner-dates__arrow" aria-hidden="true" />
            <DateTile date={end} label="Last day off" />
          </>
        )}
      </div>
      <div className="timeoff-planner-dates__facts">{facts.join(' · ')}</div>
    </div>
  );
};

// The two kinds of request a plan turns into, as the Submit step shows them.
const SUBMIT_KINDS = {
  entered: {
    chip: 'Entered',
    title: 'No review needed',
    how: 'Recorded straight away. Not paid from PATO.',
    needsApproval: false,
  },
  pato: {
    chip: 'Needs review',
    title: 'Needs review',
    how: 'Sent for approval, then paid from your PATO balance.',
    needsApproval: true,
  },
};

// The comment each kind of request starts with. Notes are required on a
// request, and these say where it came from and what covers it.
const defaultSubmitComment = (kind, plan) => {
  const trip = formatTripRange(plan.start, plan.end);
  if (kind === 'pato') {
    return `Vacation ${trip}. Planned with the Vacation Planner - ${hrs(plan.patoHours)} of PATO.`;
  }
  const covers = [
    plan.reductionChosen > 0 ? 'reduction hours' : null,
    plan.extraHours > 0 ? `${hrs(plan.extraHours)} of extra hours worked` : null,
    plan.unpaidHours > 0 ? `${hrs(plan.unpaidHours)} unpaid` : null,
  ].filter(Boolean);
  return `Vacation ${trip}. Planned with the Vacation Planner - covered by ${covers.join(', ') || 'reduction hours'}.`;
};

// Two big buttons for a yes-or-no question; null until answered.
const YesNo = ({ value, onChange, yesLabel, label }) => (
  <div className="timeoff-planner-choice" role="radiogroup" aria-label={label}>
    {[{ value: true, text: yesLabel }, { value: false, text: 'No' }].map((option) => (
      <button
        key={String(option.value)}
        type="button"
        role="radio"
        aria-checked={value === option.value}
        className={`timeoff-planner-choice__option${value === option.value ? ' timeoff-planner-choice__option--on' : ''}`}
        onClick={() => onChange(option.value)}
      >
        {option.text}
      </button>
    ))}
  </div>
);

// A labelled figure in a sum: "24 hrs / total time off".
const Figure = ({ value, label, tone = null }) => (
  <div className="timeoff-planner-figure">
    <div className={`timeoff-planner-figure__value bdc-num${tone ? ` bdc-text-${tone}` : ''}`}>{value}</div>
    <div className="timeoff-planner-figure__label">{label}</div>
  </div>
);

const Op = ({ children }) => (
  <div className="timeoff-planner-equation__op" aria-hidden="true">{children}</div>
);

const PatoTerm = () => (
  <Whisper placement="top" trigger="hover" speaker={<Tooltip>Paid Any Time Off</Tooltip>}>
    <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>PATO</span>
  </Whisper>
);

const commitmentLabel = (commitment) => {
  if (commitment.nonExempt) {
    return commitment.targetHours == null
      ? 'Non-exempt, no target set'
      : `Non-exempt, ${hrs(commitment.targetHours)} target`;
  }
  return `${formatHours(commitment.tawhPercent)}% commitment`;
};

export default function TimeOffPlannerPage() {
  const auth = useAuth();
  const employee = auth.employee || null;
  const employeeId = employee?.id ? String(employee.id) : null;

  const requestsQuery = useTimeOffRequests({ enabled: Boolean(employeeId) });
  const closeDatesQuery = useCloseDates({ enabled: true });
  const commitmentsQuery = useCommitmentHistory('me', { enabled: Boolean(employeeId) });

  const [step, setStep] = useState(0);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [hoursInput, setHoursInput] = useState(null);
  // Which Details question is open: 0 workday, 1 days and close dates, 2 total.
  const [detailsStage, setDetailsStage] = useState(0);
  // Which Plan question is open: 0 reduction, 1 extra hours, 2 PATO needed,
  // 3 the entries to make.
  const [planStage, setPlanStage] = useState(0);
  // Reduction hours to use; null means all of it.
  const [reductionInput, setReductionInput] = useState(null);
  // Set when a figure over the maximum was cut back, so the warning outlasts it.
  const [reductionCapped, setReductionCapped] = useState(false);
  // Whether they plan to work extra hours: null until they say.
  const [wantsExtra, setWantsExtra] = useState(null);
  const [extraByPeriod, setExtraByPeriod] = useState({});
  // Whether they want to take some of it unpaid: null until they say.
  const [wantsUnpaid, setWantsUnpaid] = useState(null);
  const [unpaidInput, setUnpaidInput] = useState(null);
  const [unpaidCapped, setUnpaidCapped] = useState(false);

  // Saved plans. `openPlan` is the one being looked at, if any - its id, name,
  // when it was saved, and its answers as saved, to tell whether they have
  // been changed since. `planName` is null until somebody types a name.
  const plansQuery = useVacationPlans({ enabled: Boolean(employeeId) });
  const savePlanMutation = useSaveVacationPlan();
  const [openPlan, setOpenPlan] = useState(null);
  const [planName, setPlanName] = useState(null);

  /*
    Submitting. `submission` is the set of requests frozen when Preview was
    pressed: once one is created those days count as already requested, and
    the live plan would redraw the list mid-submit. Each item is one request
    (a run of days from groupDaysIntoRequests). `submitStatus` is per item:
    submitting, done or error.
  */
  const navigate = useNavigate();
  const { planId } = useParams();
  const createRequest = useCreateTimeOffRequest();
  const recordSubmission = useRecordPlanSubmission();
  const [submission, setSubmission] = useState(null);
  const [submitComments, setSubmitComments] = useState({ entered: '', pato: '' });
  const [submitCalendar, setSubmitCalendar] = useState({ entered: true, pato: true });
  const [submitStatus, setSubmitStatus] = useState({});
  const [submitting, setSubmitting] = useState(null);
  const anySubmitted = Boolean(submission)
    && submission.items.some((item) => submitStatus[item.key]?.state === 'done');
  const activeStageRef = useRef(null);

  /*
    The working card. `reveal` is the `${scope}:${stage}` being worked out, or
    null; scope is 'step', 'details' or 'plan'. It stays up for at least
    REVEAL_MS, and longer while what the next screen needs is still loading
    (see revealWaiting below).
  */
  const [reveal, setReveal] = useState(null);
  const [revealMinElapsed, setRevealMinElapsed] = useState(false);
  const [revealTick, setRevealTick] = useState(0);
  const revealTimers = useRef({ timeout: null, interval: null });

  const clearRevealTimers = useCallback(() => {
    window.clearTimeout(revealTimers.current.timeout);
    window.clearInterval(revealTimers.current.interval);
    revealTimers.current = { timeout: null, interval: null };
  }, []);

  useEffect(() => clearRevealTimers, [clearRevealTimers]);

  // The furthest question reached in Details and in Plan. Everything up to it
  // stays on screen - folded when it is not the open one - so going back and
  // forth never hides what has already been seen.
  const [stepReached, setStepReached] = useState(0);
  const [detailsReached, setDetailsReached] = useState(0);
  const [planReached, setPlanReached] = useState(0);

  // Working cards already sat through. Each plays once; after that the
  // section comes straight up.
  const seenReveals = useRef(new Set());

  const setScopeStage = (scope, stage) => {
    if (scope === 'step') {
      setStep(stage);
      setStepReached((reached) => Math.max(reached, stage));
    } else if (scope === 'details') {
      setDetailsStage(stage);
      setDetailsReached((reached) => Math.max(reached, stage));
    } else {
      setPlanStage(stage);
      setPlanReached((reached) => Math.max(reached, stage));
    }
  };

  const tripYear = rangeStart ? rangeStart.getFullYear() : new Date().getFullYear();
  const patoQuery = useEmployeePato(employeeId, tripYear, {
    enabled: PAYROLL_ENABLED && Boolean(employeeId) && step === 2,
  });

  const profileWorkday = Number(employee?.average_workday_hours);
  const hasProfileWorkday = Number.isFinite(profileWorkday) && profileWorkday > 0;
  const suggestedHours = hasProfileWorkday ? profileWorkday : STANDARD_WORK_DAY_HOURS;
  const hoursPerDay = hoursInput == null || hoursInput === '' ? suggestedHours : Number(hoursInput);
  const hoursValid = Number.isFinite(hoursPerDay) && hoursPerDay > 0 && hoursPerDay <= MAX_HOURS_PER_DAY;

  const myRequests = useMemo(
    () => (requestsQuery.data || []).filter((row) => String(row.employee_id) === employeeId),
    [employeeId, requestsQuery.data]
  );
  const recordedRequestIds = new Set((openPlan?.submission?.requestIds || []).map(String));
  const unrecordedPlanRequestIds = openPlan
    ? myRequests
      .filter((request) => String(plannerAllocationForRequest(request)?.planId || '') === String(openPlan.id))
      .map((request) => String(request.id))
      .filter((id) => id && !recordedRequestIds.has(id))
    : [];
  const editsLocked = Boolean(submitting)
    || Boolean(openPlan?.submission?.requestIds?.length)
    || anySubmitted
    || unrecordedPlanRequestIds.length > 0;

  /*
    A non-exempt close date pays their recent daily average, which only the
    timesheet validator knows (it reads the last four weeks from TimeCamp). A
    close date anywhere in a pay period touched by the trip affects its
    reduction, so check those whole periods, not just dates in the trip.
  */
  const needsRecentAverage = useMemo(() => {
    if (!rangeStart || !rangeEnd) return false;
    const touchedPeriods = new Set();
    const cursor = new Date(rangeStart);
    while (cursor <= rangeEnd) {
      touchedPeriods.add(payPeriodFor(cursor).id);
      cursor.setDate(cursor.getDate() + 1);
    }
    return [...closeDateKeySet(closeDatesQuery.data)]
      .some((key) => touchedPeriods.has(payPeriodFor(key).id)
        && commitmentForPeriod(commitmentsQuery.data, payPeriodFor(key).id, employee).nonExempt);
  }, [closeDatesQuery.data, commitmentsQuery.data, employee, rangeEnd, rangeStart]);

  const currentPeriod = useMemo(() => getCurrentPayPeriod(), []);
  const validatorQuery = useTimesheetValidator(
    { employeeId, start: toDateOnlyString(currentPeriod.start), end: toDateOnlyString(currentPeriod.end) },
    { enabled: needsRecentAverage && step >= 1 }
  );
  const recentWeeklyAverage = validatorQuery.data?.recentWeeklyAverage || null;
  const recentDailyAverage = Number(recentWeeklyAverage?.hoursPerWeek) > 0
    ? Number(recentWeeklyAverage.hoursPerWeek) / WEEK_WORKING_DAYS
    : null;
  const recentAverageLoading = needsRecentAverage && validatorQuery.isLoading;

  const loading = requestsQuery.isLoading || closeDatesQuery.isLoading || commitmentsQuery.isLoading;
  const loadError = requestsQuery.error || closeDatesQuery.error || commitmentsQuery.error;

  // What each working card waits on beyond its minimum time - the data the
  // next screen genuinely needs.
  const waitingFor = (key) => (
    (key === 'step:1' && (loading || recentAverageLoading))
    || (key === 'details:1' && recentAverageLoading)
    || (key === 'step:2' && (loading || (PAYROLL_ENABLED && patoQuery.isLoading)))
  );
  const revealWaiting = reveal != null && waitingFor(reveal);

  /*
    Forward: fold the current question and work out the next one.

    The first time a section is reached its working card plays for
    REVEAL_MS. After that the section comes straight up - unless what it
    needs is really still loading, in which case the card stays only as long
    as that takes.
  */
  const advance = (scope, stage) => {
    if (submitting) return;
    const key = `${scope}:${stage}`;
    clearRevealTimers();
    setScopeStage(scope, stage);
    setRevealTick(0);

    if (seenReveals.current.has(key)) {
      if (waitingFor(key)) {
        setReveal(key);
        setRevealMinElapsed(true);
      } else {
        setReveal(null);
      }
      return;
    }

    seenReveals.current.add(key);
    setReveal(key);
    setRevealMinElapsed(false);
    revealTimers.current = {
      timeout: window.setTimeout(() => setRevealMinElapsed(true), REVEAL_MS),
      interval: window.setInterval(() => setRevealTick((tick) => tick + 1), REVEAL_MESSAGE_MS),
    };
  };

  // Back, or reopening a folded question: straight there.
  const goBack = (scope, stage) => {
    if (editsLocked) return;
    clearRevealTimers();
    setReveal(null);
    setScopeStage(scope, stage);
  };

  useEffect(() => {
    if (reveal == null || !revealMinElapsed || revealWaiting) return;
    clearRevealTimers();
    setReveal(null);
  }, [clearRevealTimers, revealMinElapsed, revealWaiting, reveal]);

  const isRevealing = (scope, stage) => reveal === `${scope}:${stage}`;
  const revealMessages = REVEAL_MESSAGES[reveal] || [];
  const revealMessage = revealMessages[Math.min(revealTick, revealMessages.length - 1)] || 'Working…';

  // Bring each newly revealed question into view as it fades in.
  useEffect(() => {
    if (step === 0) return;
    activeStageRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [detailsStage, planStage, reveal, step]);

  const plan = useMemo(() => {
    if (!rangeStart || !rangeEnd) return null;
    return buildVacationPlan({
      start: formatDateOnly(rangeStart),
      end: formatDateOnly(rangeEnd),
      hoursPerDay: hoursValid ? hoursPerDay : 0,
      commitments: commitmentsQuery.data || [],
      fallbackCommitment: employee,
      // The workday just confirmed is the answer the projection falls back to
      // when there is no recent average.
      averageWorkdayHours: hoursValid ? hoursPerDay : null,
      recentDailyAverage,
      closeDates: closeDatesQuery.data || [],
      requests: myRequests,
      reductionHours: reductionInput === '' ? null : reductionInput,
      // Only once they have said they will work extra.
      extraHoursByPeriod: wantsExtra ? extraByPeriod : {},
      unpaidHours: wantsUnpaid ? unpaidInput : 0,
    });
  }, [
    closeDatesQuery.data, commitmentsQuery.data, employee, extraByPeriod, hoursPerDay, hoursValid, myRequests,
    rangeEnd, rangeStart, recentDailyAverage, reductionInput, unpaidInput, wantsExtra, wantsUnpaid,
  ]);

  const closeDays = useMemo(() => (plan ? plan.days.filter((day) => day.isClose) : []), [plan]);

  // How a close date's hours were worked out, in words.
  const closeRuleFor = (day) => {
    const commitment = commitmentForPeriod(commitmentsQuery.data, day.periodId, employee);
    if (!commitment.nonExempt) {
      const exact = STANDARD_WORK_DAY_HOURS * (commitment.tawhPercent / 100);
      return {
        nonExempt: false,
        text: `${formatHours(commitment.tawhPercent)}% of an ${STANDARD_WORK_DAY_HOURS} hr day is ${formatHours(exact)} hrs, rounded up to the hour.`,
      };
    }
    if (recentDailyAverage != null) {
      return {
        nonExempt: true,
        text: `Your average over the last ${recentWeeklyAverage.weeks} weeks: ${formatHours(recentWeeklyAverage.hoursPerWeek)} hrs/week`
          + ` / ${WEEK_WORKING_DAYS} days = ${formatHours(recentDailyAverage)} hrs, rounded up to the hour.`,
      };
    }
    return {
      nonExempt: true,
      text: 'No recent TimeCamp history to average, so your standard workday, rounded up to the hour.',
    };
  };

  const enteredRuns = useMemo(() => (plan ? groupDaysIntoRequests(plan.days, 'entered') : []), [plan]);
  const patoRuns = useMemo(() => (plan ? groupDaysIntoRequests(plan.days, 'pato') : []), [plan]);
  const enteredRunHours = enteredRuns.reduce((sum, run) => sum + run.hours, 0);
  // Days in a run that are part reduction, part PATO.
  const splitDaysIn = (run) => (plan ? plan.days : []).filter((day) => (
    day.date >= run.start && day.date <= run.end && day.enteredHours > 0 && day.patoHours > 0
  ));

  // PATO already asked for but not yet taken: TimeCamp has not seen it, so the
  // ledger's remaining figure does not include it yet.
  /*
    The employee's other saved plans still ahead, for "what if I take those
    too" (on by default). Their PATO is worked out as the plans page does it
    (utils/patoOutlook.js): a trip already behind us is in the balance and not
    counted, one under way counts only its days still to come, and only days
    in this trip's balance year count. The plan open here is left out - it is
    the trip being planned.
  */
  const [includeOtherPlans, setIncludeOtherPlans] = useState(true);
  const outlookToday = formatDateOnly(new Date());
  const outlookYear = Number(outlookToday.slice(0, 4));
  // The PATO report (not the balance) is what carries dated bookings - which
  // days TimeCamp has already taken PATO for.
  const patoReportQuery = useEmployeePatoReport(employeeId, outlookYear, {
    enabled: PAYROLL_ENABLED && Boolean(employeeId) && step === 2,
  });
  const closeKeys = useMemo(() => closeDateKeySet(closeDatesQuery.data), [closeDatesQuery.data]);
  const otherPlans = useMemo(() => {
    const others = (Array.isArray(plansQuery.data) ? plansQuery.data : [])
      .filter((row) => String(row.id) !== String(openPlan?.id ?? ''));
    const outlook = buildPatoOutlook({
      plans: others,
      reportsByYear: patoReportQuery.data ? new Map([[outlookYear, patoReportQuery.data]]) : new Map(),
      requests: myRequests,
      closeKeys,
      todayIso: outlookToday,
    });
    // Each plan's PATO still to come in this trip's year, date by date, so a
    // plan crossing into the next year only counts its share.
    const ahead = others
      .map((row) => {
        const pato = outlook.perPlan.get(row.id);
        const hours = pato && pato.timing !== 'past'
          ? Object.entries(pato.remainingByDate || {})
            .filter(([date]) => Number(date.slice(0, 4)) === tripYear)
            .reduce((sum, [, value]) => sum + value, 0)
          : 0;
        return { row, pato, hours: Math.round(hours * 100) / 100 };
      })
      .filter((item) => item.hours > 0)
      .sort((a, b) => String(a.row.start_date).localeCompare(String(b.row.start_date)));
    return {
      ahead,
      hours: Math.round(ahead.reduce((sum, item) => sum + item.hours, 0) * 100) / 100,
      // Requests those plans made - recorded on the plan, or marked with it by
      // the planner - are counted as the plans, so not again as requests.
      planIds: new Set(others.map((row) => String(row.id))),
      requestIds: new Set(others.flatMap((row) => (row.submission?.requestIds || []).map(String))),
    };
  }, [closeKeys, myRequests, openPlan?.id, outlookToday, outlookYear, patoReportQuery.data, plansQuery.data, tripYear]);
  const countOtherPlans = includeOtherPlans && otherPlans.ahead.length > 0;

  // PATO already asked for but not yet taken, in this trip's year, day by day
  // and less what TimeCamp has already booked on each day: the ledger's
  // remaining figure has those, not the rest. With other plans counted, their
  // own requests are left to them.
  const patoRequestedAhead = useMemo(() => {
    const bookedByDate = new Map();
    if (tripYear === outlookYear) {
      (patoReportQuery.data?.events || []).forEach((event) => {
        if (event.kind !== 'pato' || !event.date) return;
        const date = String(event.date).slice(0, 10);
        bookedByDate.set(date, (bookedByDate.get(date) || 0) + (Number(event.hours) || 0));
      });
    }
    return myRequests.reduce((total, request) => {
      if (!isPatoRequest(request)) return total;
      if (countOtherPlans && (
        otherPlans.requestIds.has(String(request.id))
        || otherPlans.planIds.has(String(plannerAllocationForRequest(request)?.planId || ''))
      )) return total;
      let sum = total;
      requestHoursByDay(request, closeKeys).forEach((hours, date) => {
        if (date < outlookToday || Number(date.slice(0, 4)) !== tripYear) return;
        sum += Math.max(0, hours - (bookedByDate.get(date) || 0));
      });
      return sum;
    }, 0);
  }, [closeKeys, countOtherPlans, myRequests, otherPlans.planIds, otherPlans.requestIds, outlookToday, outlookYear, patoReportQuery.data, tripYear]);

  const patoUnlimited = Boolean(patoQuery.data?.unlimited);
  const patoRemaining = patoQuery.data?.remainingHours ?? null;
  const patoInTripYear = plan
    ? Math.round((plan.days
      .filter((day) => Number(day.date.slice(0, 4)) === tripYear)
      .reduce((sum, day) => sum + day.patoHours, 0) + Number.EPSILON) * 100) / 100
    : 0;
  const patoAfter = patoRemaining == null || !plan
    ? null
    : patoRemaining - patoRequestedAhead - (countOtherPlans ? otherPlans.hours : 0) - patoInTripYear;
  const tripCrossesYear = Boolean(rangeStart && rangeEnd && rangeStart.getFullYear() !== rangeEnd.getFullYear());

  // Periods where extra hours could still take PATO off.
  const extraPeriods = plan ? plan.periods.filter((period) => period.afterReduction > 0) : [];

  /*
    What a saved plan stores: the answers, never the results. Reopening one
    works the plan out again against today's data, so it cannot go stale.
    Numbers are tidied here so a half-typed "7." saves as 7 and an untouched
    plan compares equal to what was saved.
  */
  const currentInputs = useMemo(() => {
    const extras = {};
    Object.entries(extraByPeriod || {}).forEach(([periodId, value]) => {
      const n = tidyNumber(value);
      if (n != null && n > 0) extras[periodId] = n;
    });
    return {
      version: 1,
      hoursPerDay: hoursValid ? hoursPerDay : null,
      reductionHours: tidyNumber(reductionInput),
      wantsExtra,
      extraByPeriod: extras,
      wantsUnpaid,
      unpaidHours: tidyNumber(unpaidInput),
    };
  }, [extraByPeriod, hoursPerDay, hoursValid, reductionInput, unpaidInput, wantsExtra, wantsUnpaid]);

  const startIso = rangeStart ? formatDateOnly(rangeStart) : null;
  const endIso = rangeEnd ? formatDateOnly(rangeEnd) : null;
  const defaultPlanName = startIso && endIso ? formatTripRange(startIso, endIso) : 'My vacation';
  const effectivePlanName = (planName ?? openPlan?.name ?? defaultPlanName).trim();
  const currentSnapshot = stableStringify({ start: startIso, end: endIso, inputs: currentInputs });
  const planDirty = Boolean(openPlan) && (
    currentSnapshot !== openPlan.snapshot || effectivePlanName !== openPlan.name
  );

  const rememberOpenPlan = (row) => {
    setOpenPlan({
      id: row.id,
      name: row.name,
      updatedAt: row.updated_at,
      summary: row.summary || {},
      submission: row.submission || {},
      snapshot: stableStringify({ start: row.start_date, end: row.end_date, inputs: row.inputs }),
    });
    setPlanName(null);
  };

  const savePlan = async ({ asNew = false, quiet = false } = {}) => {
    if (!plan || !startIso || !endIso) return null;
    const preserveSubmittedAllocation = !asNew && Boolean(openPlan?.submission?.requestIds?.length);
    // What the plan counts on in each pay period - extra hours to work, time
    // taken unpaid - for the View Hours reminders. Like the PATO by date, kept
    // as submitted: once requests exist their days read as already requested,
    // and the plan worked out again would no longer have them.
    const byPeriod = (key) => plan.periods.reduce((periods, period) => {
      if (period[key] > 0) periods[period.id] = period[key];
      return periods;
    }, {});
    const kept = (key, fresh) => (preserveSubmittedAllocation ? openPlan.summary?.[key] ?? fresh : fresh);
    try {
      const saved = await savePlanMutation.mutateAsync({
        id: asNew ? undefined : openPlan?.id,
        name: effectivePlanName || defaultPlanName,
        start_date: startIso,
        end_date: endIso,
        inputs: currentInputs,
        summary: {
          totalHours: plan.totalHours,
          reductionHours: plan.reductionChosen,
          extraHours: kept('extraHours', plan.extraHours),
          unpaidHours: kept('unpaidHours', plan.unpaidHours),
          extraByPeriod: kept('extraByPeriod', byPeriod('extraUsed')),
          unpaidByPeriod: kept('unpaidByPeriod', byPeriod('unpaidUsed')),
          enteredHours: plan.enteredHours,
          patoHours: preserveSubmittedAllocation ? openPlan.summary?.patoHours ?? plan.patoHours : plan.patoHours,
          patoByDate: preserveSubmittedAllocation
            ? openPlan.summary?.patoByDate ?? {}
            : plan.days.reduce((byDate, day) => {
              if (day.patoHours > 0) byDate[day.date] = day.patoHours;
              return byDate;
            }, {}),
        },
      });
      rememberOpenPlan(saved);
      if (!quiet) showToast('success', asNew || !openPlan ? 'Plan saved.' : 'Changes saved.');
      // A new plan gets its own address, so a refresh or a bookmark comes back
      // to it. It is open already, so the address catching up does not
      // reopen it.
      openedFromAddress.current = String(saved.id);
      if (String(planId) !== String(saved.id)) {
        navigate(`/time-off/planner/${saved.id}`, { replace: true });
      }
      return saved;
    } catch (error) {
      showToast('error', error?.message || 'The plan could not be saved.');
      return null;
    }
  };

  // Every working card, already "seen": a reopened plan has been through
  // them all.
  const ALL_REVEAL_KEYS = Object.keys(REVEAL_MESSAGES);

  // Puts a saved plan's answers back and lands on its entries, every section
  // reached and folded above.
  const openSavedPlan = (row) => {
    const inputs = row.inputs || {};
    clearRevealTimers();
    setReveal(null);
    setRangeStart(parseLocalDateOnly(row.start_date));
    setRangeEnd(parseLocalDateOnly(row.end_date));
    setHoursInput(inputs.hoursPerDay ?? null);
    setReductionInput(inputs.reductionHours ?? null);
    setReductionCapped(false);
    setWantsExtra(inputs.wantsExtra ?? null);
    setExtraByPeriod(inputs.extraByPeriod || {});
    setWantsUnpaid(inputs.wantsUnpaid ?? null);
    setUnpaidInput(inputs.unpaidHours ?? null);
    setUnpaidCapped(false);
    setDetailsStage(2);
    setStepReached(2);
    setDetailsReached(2);
    setPlanStage(3);
    setPlanReached(3);
    seenReveals.current = new Set(ALL_REVEAL_KEYS);
    rememberOpenPlan(row);
    setStep(2);
  };

  /*
    The plan in the address: /time-off/planner/new for a fresh one, or a saved
    plan's id. A saved plan opens once the list it comes from has loaded.
  */
  const isNewPlan = !planId || planId === 'new';
  const savedPlans = Array.isArray(plansQuery.data) ? plansQuery.data : [];
  const openedFromAddress = useRef(null);
  useEffect(() => {
    if (isNewPlan || openedFromAddress.current === String(planId)) return;
    const row = savedPlans.find((candidate) => String(candidate.id) === String(planId));
    if (!row) return;
    openedFromAddress.current = String(planId);
    openSavedPlan(row);
    // Keyed on the address and the list; openSavedPlan only sets state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewPlan, planId, plansQuery.data]);
  const planNotFound = !isNewPlan
    && plansQuery.isSuccess
    && !savedPlans.some((candidate) => String(candidate.id) === String(planId));
  const waitingForPlan = !isNewPlan && !openPlan && !planNotFound;

  // Freezes the plan's entries into requests and opens the Submit step.
  const previewRequests = () => {
    if (!plan) return;
    const toItems = (kind, runs) => runs.map((run) => ({
      key: `${kind}-${run.start}`,
      kind,
      start: run.start,
      end: run.end,
      hours: run.hours,
      days: run.days,
      allocations: plan.days
        .filter((day) => day.date >= run.start && day.date <= run.end && (
          kind === 'pato' ? day.patoHours > 0 : day.patoHours === 0 && day.enteredHours > 0
        ))
        .map((day) => ({
          date: day.date,
          hours: kind === 'pato' ? day.patoHours : day.enteredHours,
          reductionHours: kind === 'pato' ? day.enteredHours : 0,
          isClose: day.isClose,
        })),
      // Days in the run that are part reduction: said on the request, since
      // only their PATO hours are on it.
      splitDays: kind === 'pato'
        ? splitDaysIn(run).map((day) => ({ date: day.date, enteredHours: day.enteredHours, patoHours: day.patoHours }))
        : [],
    }));
    const items = [...toItems('entered', enteredRuns), ...toItems('pato', patoRuns)];
    const recordedIds = [...recordedRequestIds];
    const priorTotal = Number(openPlan?.submission?.total) || 0;
    setSubmission({
      items,
      plan: { start: plan.start, end: plan.end, totalHours: plan.totalHours },
      snapshot: currentSnapshot,
      total: Math.max(priorTotal, recordedIds.length + unrecordedPlanRequestIds.length + items.length),
      createdIds: unrecordedPlanRequestIds,
    });
    setSubmitComments({ entered: defaultSubmitComment('entered', plan), pato: defaultSubmitComment('pato', plan) });
    setSubmitCalendar({ entered: true, pato: true });
    setSubmitStatus({});
    advance('step', 3);
  };

  const submitItem = async (item, planRowId) => {
    const comment = submitComments[item.kind].trim();
    const notes = [
      comment,
      ...item.splitDays.map((day) => (
        `${formatShortDate(day.date)}: ${hrs(day.enteredHours)} reduction and ${hrs(day.patoHours)} PATO - only the PATO hours are on this request.`
      )),
      `[[VACATION-PLANNER-ALLOCATION]]${JSON.stringify({ planId: planRowId, days: item.allocations })}`,
    ].join('\n');

    setSubmitStatus((prev) => ({ ...prev, [item.key]: { state: 'submitting' } }));
    try {
      const created = await createRequest.mutateAsync({
        employee_id: employeeId,
        request_type: 'time off',
        start_date: item.start,
        end_date: item.end,
        notes,
        total_hours: item.hours,
        start_minutes: null,
        end_minutes: null,
        needs_approval: SUBMIT_KINDS[item.kind].needsApproval,
        add_to_company_calendar: submitCalendar[item.kind],
      });
      setSubmitStatus((prev) => ({ ...prev, [item.key]: { state: 'done', requestId: created?.id ? String(created.id) : null } }));
      return created?.id ? String(created.id) : true;
    } catch (error) {
      setSubmitStatus((prev) => ({
        ...prev,
        [item.key]: { state: 'error', message: error?.message || 'This request could not be submitted.' },
      }));
      return false;
    }
  };

  // One kind, or everything ('all'), one request at a time so each gets its
  // own result. Anything already submitted is skipped, so a retry only sends
  // what failed.
  const submitKind = async (kind) => {
    if (!submission || submitting || submission.snapshot !== currentSnapshot) return;
    const items = submission.items.filter((item) => (
      (kind === 'all' || item.kind === kind) && submitStatus[item.key]?.state !== 'done'
    ));
    const unrecordedIds = [...new Set((submission.createdIds || []).map(String))];
    if (items.length === 0 && unrecordedIds.length === 0) return;
    setSubmitting(kind);

    /*
      The plan is saved before its first request goes, so the list of plans
      can show it as submitted. Before, not after: once a request exists its
      days count as already requested and the plan's figures redraw without
      them. Only on the first go - a later one is the same plan, already saved.
    */
    let planRowId = openPlan?.id || null;
    const firstGo = !submission.items.some((item) => submitStatus[item.key]?.state === 'done');
    if (firstGo && (!openPlan || planDirty)) {
      const saved = await savePlan({ quiet: true });
      if (saved?.id) planRowId = saved.id;
    }
    if (firstGo && !planRowId) {
      setSubmitting(null);
      return;
    }

    let failed = 0;
    const createdIds = unrecordedIds;
    for (const item of items) {
      // eslint-disable-next-line no-await-in-loop
      const result = await submitItem(item, planRowId);
      if (!result) failed += 1;
      else if (typeof result === 'string') {
        createdIds.push(result);
        setSubmission((previous) => ({
          ...previous,
          createdIds: [...new Set([...(previous?.createdIds || []), result])],
        }));
      }
    }

    if (planRowId && createdIds.length > 0) {
      try {
        const saved = await recordSubmission.mutateAsync({
          id: planRowId,
          request_ids: createdIds,
          total: submission.total,
        });
        setOpenPlan((previous) => previous && String(previous.id) === String(planRowId)
          ? { ...previous, submission: saved.submission || previous.submission, summary: saved.summary || previous.summary }
          : previous);
        setSubmission((previous) => ({ ...previous, createdIds: [] }));
      } catch {
        showToast('warning', 'Your requests went in, but the plan could not be marked as submitted.');
      }
    }

    setSubmitting(null);
    if (failed === 0) {
      showToast('success', `${plural(items.length, 'request')} submitted.`);
    } else {
      showToast('error', `${plural(failed, 'request')} could not be submitted. See the details below.`);
    }
  };

  const itemsOf = (kind) => (submission ? submission.items.filter((item) => item.kind === kind) : []);
  const remainingOf = (kind) => itemsOf(kind).filter((item) => submitStatus[item.key]?.state !== 'done');
  const allSubmitted = Boolean(submission)
    && submission.items.length > 0
    && submission.items.every((item) => submitStatus[item.key]?.state === 'done');
  const previewStale = Boolean(submission) && submission.snapshot !== currentSnapshot;
  const retrySubmissionRecording = async () => {
    if (!openPlan?.id || !submission?.createdIds?.length || submitting) return;
    setSubmitting('recording');
    try {
      const saved = await recordSubmission.mutateAsync({
        id: openPlan.id,
        request_ids: submission.createdIds,
        total: submission.total,
      });
      setOpenPlan((previous) => previous && String(previous.id) === String(openPlan.id)
        ? { ...previous, submission: saved.submission || previous.submission, summary: saved.summary || previous.summary }
        : previous);
      setSubmission((previous) => ({ ...previous, createdIds: [] }));
      showToast('success', 'The plan is now linked to your submitted requests.');
    } catch {
      showToast('warning', 'Your requests went in, but the plan could not be marked as submitted.');
    } finally {
      setSubmitting(null);
    }
  };

  const startOver = () => {
    setSubmission(null);
    setSubmitStatus({});
    setRangeStart(null);
    setRangeEnd(null);
    setHoursInput(null);
    setReductionInput(null);
    setReductionCapped(false);
    setWantsExtra(null);
    setExtraByPeriod({});
    setWantsUnpaid(null);
    setUnpaidInput(null);
    setUnpaidCapped(false);
    goBack('details', 0);
    goBack('plan', 0);
    goBack('step', 0);
    setStepReached(0);
    setDetailsReached(0);
    setPlanReached(0);
    seenReveals.current = new Set();
    setOpenPlan(null);
    setPlanName(null);
  };

  // The dates, read only. They are changed back on the calendar.
  const datesStrip = rangeStart && rangeEnd && plan ? (
    <DatesSummary
      start={rangeStart}
      end={rangeEnd}
      totalDays={plan.days.length}
      workingDays={plan.weekdays}
      hoursPerDay={step === 2 ? plan.hoursPerDay : null}
    />
  ) : null;

  const stepWorking = (target) => isRevealing('step', target);

  const header = (
    <PageHeader
      crumbs={[
        { label: 'Home', to: '/' },
        { label: 'Time Off', to: '/time-off' },
        { label: 'Vacation Planner', to: '/time-off/planner' },
        { label: openPlan ? openPlan.name : (isNewPlan ? 'New plan' : 'Plan') },
      ]}
      icon="bi-airplane"
      title={openPlan ? openPlan.name : 'New Vacation Plan'}
    />
  );

  // Opening a saved plan from its address: nothing to show until it has loaded,
  // and nothing to plan if it is not there.
  if (waitingForPlan || planNotFound) {
    return (
      <div className="bdc-page">
        {header}
        <VacationPlannerNotice />
        {planNotFound ? (
          <Message type="warning" showIcon>
            This plan could not be found - it may have been deleted.{' '}
            <Link to="/time-off/planner">Back to your plans</Link>
          </Message>
        ) : plansQuery.isError ? (
          <Message type="error" showIcon>
            {String(plansQuery.error?.message || 'Your saved plans could not be loaded.')}
          </Message>
        ) : (
          <div className="timeoff-planner-details">
            <WorkingCard message="Opening your plan…" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bdc-page">
      {header}
      <VacationPlannerNotice />

      <ol className="timeoff-planner-steps" aria-label="Planner steps">
        {STEPS.map((label, index) => {
          let state = 'todo';
          if (index === step) state = 'current';
          else if (index < step) state = 'done';
          else if (index <= stepReached) state = 'reached';
          // Any step reached before can be gone to directly - Details and
          // Plan only while there are dates (and a workday) to work from.
          const canGo = !editsLocked
            && index !== step
            && index <= stepReached
            && (index === 0 || Boolean(plan && (index === 1 || hoursValid)))
            && (index !== 3 || Boolean(submission));
          const content = (
            <>
              <span className="timeoff-planner-step__num">
                {state === 'done' ? <i className="bi bi-check2" aria-hidden="true" /> : index + 1}
              </span>
              <span className="timeoff-planner-step__label">{label}</span>
            </>
          );
          return (
            <li key={label} className={`timeoff-planner-step timeoff-planner-step--${state}`} aria-current={index === step ? 'step' : undefined}>
              {canGo ? (
                <button type="button" className="timeoff-planner-step__go" onClick={() => goBack('step', index)}>
                  {content}
                </button>
              ) : content}
            </li>
          );
        })}
      </ol>

      {openPlan && step > 0 ? (
        <div className="timeoff-planner-openplan">
          <i className="bi bi-bookmark-fill" aria-hidden="true" />
          <span className="timeoff-planner-openplan__name">{openPlan.name}</span>
          <span className={`timeoff-planner-openplan__state${planDirty ? ' timeoff-planner-openplan__state--dirty' : ''}`}>
            {planDirty ? 'Unsaved changes' : `Saved ${formatSavedAt(openPlan.updatedAt)}`}
          </span>
        </div>
      ) : null}

      {loadError ? (
        <Message type="error" showIcon>
          {String(loadError?.message || loadError || 'Some of your time-off details could not be loaded.')}
        </Message>
      ) : null}

      {!employeeId ? (
        <Message type="warning" showIcon>
          Your account is not linked to an employee record, so there is nothing to plan against.
        </Message>
      ) : null}

      {step === 0 ? (
        <Panel bordered className="timeoff-create-calendar-panel">
          <TimeOffRangeCalendar
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            onRangeChange={(start, end) => {
              setRangeStart(start);
              setRangeEnd(end);
            }}
            requests={requestsQuery.data || []}
            closeDates={closeDatesQuery.data || []}
            employeeId={employeeId}
          />

          <div className="d-flex justify-content-end mt-3">
            <Button
              appearance="primary"
              size="lg"
              disabled={!rangeStart || !rangeEnd || !employeeId}
              onClick={() => {
                // New dates mean the days have to be counted again, but a
                // confirmed workday stands.
                setDetailsStage((stage) => Math.min(stage, 1));
                advance('step', 1);
              }}
            >
              Next
            </Button>
          </div>
        </Panel>
      ) : null}

      {(step === 1 || step === 2) && stepWorking(step) ? (
        <div className="timeoff-planner-details">
          {datesStrip}
          <WorkingCard key={`step-working-${step}`} cardRef={activeStageRef} message={revealMessage} />
        </div>
      ) : null}

      {step === 1 && plan && !stepWorking(1) ? (
        /* Details: one question at a time. Each answer folds down to a line
           with a Change link, and the next thing fades in under it. */
        <div className="timeoff-planner-details">
          {datesStrip}

          {detailsStage === 0 ? (
            <form
              key="workday-ask"
              ref={activeStageRef}
              className="timeoff-planner-card timeoff-planner-reveal"
              onSubmit={(event) => {
                event.preventDefault();
                if (hoursValid) advance('details', 1);
              }}
            >
              <h2 className="timeoff-planner-ask">How many hours do you work in a standard workday?</h2>
              <p className="timeoff-planner-sub">
                {hasProfileWorkday
                  ? 'We filled in the usual workday from your profile.'
                  : `Your profile does not have a usual workday yet, so we filled in a standard ${hrs(STANDARD_WORK_DAY_HOURS)} day.`}
                {' '}Change it if your day is different, then confirm.
              </p>
              <div className="timeoff-planner-workday">
                <InputNumber
                  id="planner-hours-per-day"
                  size="lg"
                  min={0.25}
                  max={MAX_HOURS_PER_DAY}
                  step={0.25}
                  value={hoursInput == null ? suggestedHours : hoursInput}
                  onChange={(value) => setHoursInput(typedNumber(value))}
                  onBlur={() => setHoursInput((prev) => tidyNumber(prev))}
                  aria-label="Hours in a standard workday"
                  style={{ width: 150 }}
                />
                <span className="timeoff-planner-workday__unit">hrs / day</span>
              </div>
              {!hoursValid ? (
                <div className="bdc-text-bad" style={{ fontSize: 13 }}>
                  Enter a number of hours between 0.25 and {MAX_HOURS_PER_DAY}.
                </div>
              ) : null}
              <div className="timeoff-planner-actions">
                <Button type="button" appearance="ghost" size="lg" disabled={editsLocked} onClick={() => goBack('step', 0)}>
                  Back
                </Button>
                <Button type="submit" appearance="primary" size="lg" disabled={!hoursValid}>
                  Confirm
                </Button>
              </div>
            </form>
          ) : (
            <ConfirmedLine
              key="workday-done"
              disabled={editsLocked}
              label="Standard workday"
              value={hrs(hoursPerDay)}
              onChange={() => goBack('details', 0)}
            />
          )}

          {detailsStage === 1 && isRevealing('details', 1) ? (
            <WorkingCard key="days-working" cardRef={activeStageRef} message={revealMessage} />
          ) : detailsStage === 1 ? (
            <div key="days-ask" ref={activeStageRef} className="timeoff-planner-card timeoff-planner-reveal">
              <div className="timeoff-planner-equation">
                <Figure value={plan.plannedDays} label={`working day${plan.plannedDays === 1 ? '' : 's'} selected`} />
                <Op>x</Op>
                <Figure value={formatHours(hoursPerDay)} label="hrs in your day" />
                <Op>=</Op>
                <Figure value={formatHours(plan.workDayHours)} label="hrs off" />
              </div>

              {plan.requestedDays > 0 ? (
                <p className="timeoff-planner-sub">
                  {plan.requestedDays} more day{plan.requestedDays === 1 ? ' is' : 's are'} already covered by one of
                  your requests, so {plan.requestedDays === 1 ? 'it is' : 'they are'} not counted again.
                </p>
              ) : null}

              <div className="timeoff-planner-closes">
                <div className="timeoff-planner-closes__head">
                  <span className="timeoff-planner-closes__title">Close dates</span>
                  <span className="bdc-count">{closeDays.length}</span>
                </div>
                {closeDays.length === 0 ? (
                  <p className="timeoff-planner-sub">No close dates fall in your dates.</p>
                ) : (
                  <ul className="timeoff-planner-closes__list">
                    {closeDays.map((day) => (
                      <li key={day.date} className="timeoff-planner-close">
                        <div className="timeoff-planner-close__main">
                          <div className="timeoff-planner-close__name">{day.closeName}</div>
                          <div className="timeoff-planner-close__date">{formatShortDate(day.date)}</div>
                        </div>
                        <div className="timeoff-planner-close__figures">
                          <div className="timeoff-planner-close__credit">
                            <span className="bdc-num">{hrs(day.closeCredit)}</span> you can log off
                          </div>
                          <div className={`timeoff-planner-close__gap ${day.hours > 0 ? 'bdc-text-warn' : 'bdc-text-good'}`}>
                            {day.hours > 0 ? `${hrs(day.hours)} of your day to cover` : 'Covers your whole day'}
                          </div>
                        </div>
                        <div className="timeoff-planner-close__rule">{closeRuleFor(day).text}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="timeoff-planner-actions">
                <Button appearance="ghost" size="lg" disabled={editsLocked} onClick={() => goBack('details', 0)}>
                  Back
                </Button>
                <Button appearance="primary" size="lg" onClick={() => advance('details', 2)}>
                  Confirm
                </Button>
              </div>
            </div>
          ) : detailsReached >= 1 ? (
            <ConfirmedLine
              key="days-done"
              disabled={editsLocked}
              ahead={detailsStage < 1}
              label="Days"
              value={`${plural(plan.plannedDays, 'working day')} · ${plural(plan.closeDays, 'close date')}`}
              onChange={() => goBack('details', 1)}
            />
          ) : null}

          {detailsStage === 2 && isRevealing('details', 2) ? (
            <WorkingCard key="total-working" cardRef={activeStageRef} message={revealMessage} />
          ) : detailsStage === 2 ? (
            <div key="total" ref={activeStageRef} className="timeoff-planner-card timeoff-planner-reveal">
              <div className="timeoff-planner-total__label">Total time off</div>
              <div className="timeoff-planner-total__value bdc-num">{hrs(plan.totalHours)}</div>
              <p className="timeoff-planner-sub">
                {plan.totalHours === 0
                  ? 'Your close dates cover all of it - there is nothing to plan.'
                  : [
                    `${hrs(plan.workDayHours)} across ${plural(plan.plannedDays, 'working day')}`,
                    plan.closeDayHours > 0 ? `${hrs(plan.closeDayHours)} of close dates not paid by the company` : null,
                  ].filter(Boolean).join(' + ')}
              </p>

              {/* Every day of the range, weekends too, so the total can be
                  checked a day at a time. The rows arrive one after another. */}
              <ol className="timeoff-planner-daylist">
                {plan.days.map((day, index) => {
                  const info = describeDay(day, plan.hoursPerDay);
                  const date = toStartOfDay(day.date);
                  return (
                    <li
                      key={day.date}
                      className={`timeoff-planner-day timeoff-planner-day--${info.kind}`}
                      style={{ animationDelay: `${Math.min(index, DAYLIST_STAGGER_CAP) * DAYLIST_STAGGER_MS}ms` }}
                    >
                      <div className="timeoff-planner-day__date">
                        <span className="timeoff-planner-day__weekday">
                          {date.toLocaleDateString('en-US', { weekday: 'short' })}
                        </span>
                        <span>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                      <div className="timeoff-planner-day__why">
                        <div className="timeoff-planner-day__title">{info.title}</div>
                        <div className="timeoff-planner-day__detail">{info.detail}</div>
                      </div>
                      <div className="timeoff-planner-day__hours bdc-num">{hrs(day.hours)}</div>
                    </li>
                  );
                })}
                <li
                  className="timeoff-planner-day timeoff-planner-day--total"
                  style={{ animationDelay: `${Math.min(plan.days.length, DAYLIST_STAGGER_CAP) * DAYLIST_STAGGER_MS}ms` }}
                >
                  <div className="timeoff-planner-day__why">
                    <div className="timeoff-planner-day__title">Total time off</div>
                  </div>
                  <div className="timeoff-planner-day__hours bdc-num">{hrs(plan.totalHours)}</div>
                </li>
              </ol>

              <div className="timeoff-planner-actions">
                <Button appearance="ghost" size="lg" disabled={editsLocked} onClick={() => goBack('details', 1)}>
                  Back
                </Button>
                <Button
                  appearance="primary"
                  size="lg"
                  disabled={plan.totalHours === 0}
                  onClick={() => {
                    setPlanStage(0);
                    advance('step', 2);
                  }}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : detailsReached >= 2 ? (
            <ConfirmedLine
              key="total-done"
              disabled={editsLocked}
              ahead
              label="Total time off"
              value={hrs(plan.totalHours)}
              onChange={() => goBack('details', 2)}
            />
          ) : null}
        </div>
      ) : null}

      {step === 2 && plan && !stepWorking(2) ? (
        /* Plan: the same one-at-a-time column as Details. */
        <div className="timeoff-planner-details timeoff-planner-details--wide">
          {datesStrip}

          {/* 3a. Total time off, the reduction there is to put against it,
              and how much of it to use. */}
          {planStage === 0 ? (
            <div key="reduction-ask" ref={activeStageRef} className="timeoff-planner-card timeoff-planner-reveal">
              <div className="timeoff-planner-total__label">Total time off</div>
              <div className="timeoff-planner-total__value bdc-num">{hrs(plan.totalHours)}</div>

              {/* No heading of its own: one sat right above the figure and read
                  as labelling the total. The rule keeps the break. */}
              <p className="timeoff-planner-sub timeoff-planner-sub--section">
                Your commitment leaves part of every pay period free. On your standard schedule of {hrs(plan.hoursPerDay)} a
                day, this is how much of it you can put toward these dates without using <PatoTerm />.
              </p>

              {plan.periods.length > 1 ? (
                <div className="timeoff-planner-callout">
                  <i className="bi bi-stars" aria-hidden="true" />
                  <div>
                    <strong>Your dates span {plan.periods.length} pay periods.</strong> Reduction comes back every pay
                    period, so you get it from each one: {plan.periods.map((period) => `${hrs(period.usable)} from ${period.label}`).join(' + ')}.
                  </div>
                </div>
              ) : null}

              <div className="timeoff-planner-periods">
                {plan.periods.map((period) => (
                  <div key={period.id} className="timeoff-planner-period">
                    <div className="timeoff-planner-period__head">
                      <span className="timeoff-planner-period__title">Pay period {period.label}</span>
                      <span className="timeoff-planner-period__meta">
                        {commitmentLabel(period.commitment)} · {hrs(period.tripHours)} of your time off
                      </span>
                    </div>
                    <dl className="timeoff-planner-ledger">
                      <div>
                        <dt>Your schedule: {period.workingDays} days x {hrs(plan.hoursPerDay)}</dt>
                        <dd className="bdc-num">{hrs(period.scheduled)}</dd>
                      </div>
                      <div>
                        <dt>{period.commitment.nonExempt ? 'Target hours' : `TAWH at ${formatHours(period.commitment.tawhPercent)}%`}</dt>
                        <dd className="bdc-num">- {hrs(period.target)}</dd>
                      </div>
                      <div className="timeoff-planner-ledger__sub">
                        <dt>Reduction this pay period</dt>
                        <dd className="bdc-num">{hrs(period.allowance)}</dd>
                      </div>
                      {period.enteredItems.map((item) => (
                        <div key={`${item.id}-${item.start}`}>
                          <dt>
                            Already entered: {item.start === item.end
                              ? formatShortDate(item.start)
                              : `${formatShortDate(item.start)} to ${formatShortDate(item.end)}`}
                          </dt>
                          <dd className="bdc-num">- {hrs(item.hours)}</dd>
                        </div>
                      ))}
                      {period.closeShortfall > 0 ? (
                        <div>
                          <dt>
                            {plural(period.closeDays, 'other close date')} paying {hrs(period.closeDayCredit)} of your day
                          </dt>
                          <dd className="bdc-num">- {hrs(period.closeShortfall)}</dd>
                        </div>
                      ) : null}
                      <div className="timeoff-planner-ledger__total">
                        <dt>Available for your time off</dt>
                        <dd className="bdc-num">{hrs(period.usable)}</dd>
                      </div>
                    </dl>
                    {period.available > period.tripHours ? (
                      <div className="timeoff-planner-period__note">
                        {hrs(period.available)} is free here, but reduction can only cover the days you are
                        actually off - at most {hrs(plan.hoursPerDay)} a day. You are off{' '}
                        {plural(period.tripDays, 'day')} in this pay period, so {hrs(period.tripHours)} is all that
                        counts.
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="timeoff-planner-equation">
                <Figure value={formatHours(plan.totalHours)} label="hrs time off" />
                <Op>-</Op>
                <Figure value={formatHours(plan.reductionChosen)} label="hrs reduction" tone="good" />
                <Op>=</Op>
                <Figure value={formatHours(plan.afterReduction)} label="hrs left to cover" />
              </div>

              <div className="timeoff-planner-choose">
                <label className="timeoff-planner-choose__label" htmlFor="planner-reduction">
                  Reduction hours to use
                </label>
                <div className="timeoff-planner-workday">
                  <InputNumber
                    id="planner-reduction"
                    min={0}
                    step={0.25}
                    value={reductionInput == null ? plan.maxReduction : reductionInput}
                    onChange={(value) => {
                      setReductionCapped(false);
                      setReductionInput(typedNumber(value));
                    }}
                    // Never more than there is: cut back to the maximum, and
                    // say so.
                    onBlur={() => {
                      const typed = tidyNumber(reductionInput);
                      if (typed != null && typed > plan.maxReduction) {
                        setReductionInput(null);
                        setReductionCapped(true);
                      } else {
                        setReductionInput(typed);
                      }
                    }}
                    style={{ width: 130 }}
                  />
                  <span className="timeoff-planner-workday__unit">of {hrs(plan.maxReduction)}</span>
                </div>
                {plan.reductionOver > 0 || reductionCapped ? (
                  <div className="timeoff-planner-warning" role="alert">
                    <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                    You only have {hrs(plan.maxReduction)} of reduction for these dates, so you cannot use more than
                    that.
                  </div>
                ) : plan.reductionUnused > 0 ? (
                  <div className="timeoff-planner-choose__hint">
                    Keeping {hrs(plan.reductionUnused)} back.{' '}
                    <button type="button" className="bdc-linkbtn" onClick={() => setReductionInput(null)}>
                      Use all {hrs(plan.maxReduction)}
                    </button>
                  </div>
                ) : (
                  <div className="timeoff-planner-choose__hint">
                    All of it is used by default. Lower it if you want to keep some for other days in these pay periods.
                  </div>
                )}
              </div>

              <div className="timeoff-planner-actions">
                <Button appearance="ghost" size="lg" disabled={editsLocked} onClick={() => goBack('step', 1)}>
                  Back
                </Button>
                <Button appearance="primary" size="lg" onClick={() => advance('plan', 1)}>
                  Confirm
                </Button>
              </div>
            </div>
          ) : (
            <ConfirmedLine
              key="reduction-done"
              disabled={editsLocked}
              label="Reduction"
              value={`${hrs(plan.reductionChosen)} of ${hrs(plan.maxReduction)} · ${hrs(plan.afterReduction)} left to cover`}
              onChange={() => goBack('plan', 0)}
            />
          )}

          {/* 3b. What is left, and whether extra hours will take some of it. */}
          {planStage === 1 && isRevealing('plan', 1) ? (
            <WorkingCard key="extra-working" cardRef={activeStageRef} message={revealMessage} />
          ) : planStage === 1 ? (
            <div key="extra-ask" ref={activeStageRef} className="timeoff-planner-card timeoff-planner-reveal">
              {/* Live: the PATO left after the extra hours typed below, so the
                  figure moves as they are stepped up and down. */}
              <div className="timeoff-planner-total__label"><PatoTerm /> needed to cover</div>
              <div
                key={plan.patoHours}
                className={`timeoff-planner-total__value timeoff-planner-total__value--live bdc-num${plan.patoHours > 0 ? ' bdc-text-warn' : ' bdc-text-good'}`}
                aria-live="polite"
              >
                {hrs(plan.patoHours)}
              </div>
              {/* Always there once there is anything to cover, so typing extra or
                  unpaid hours changes the words rather than pushing the card
                  down. (With nothing to cover there is nothing to type.) */}
              {plan.afterReduction > 0 ? (
                <p className="timeoff-planner-sub">
                  {[
                    `${hrs(plan.afterReduction)} after reduction`,
                    plan.extraHours > 0 ? `less ${hrs(plan.extraHours)} of extra hours` : null,
                    plan.unpaidHours > 0 ? `less ${hrs(plan.unpaidHours)} unpaid` : null,
                  ].filter(Boolean).join(', ')}
                  {plan.patoHours === 0 ? ' - no PATO needed.' : ''}
                </p>
              ) : null}

              {plan.afterReduction === 0 ? (
                <p className="timeoff-planner-sub">Your reduction covers all of it - no PATO needed.</p>
              ) : (
                <>
                  <h2 className="timeoff-planner-ask timeoff-planner-ask--section">
                    Are you planning to work extra hours before your time off to offset it?
                  </h2>
                  <p className="timeoff-planner-sub">
                    Hours worked over your standard day in the same pay period take the place of PATO, hour for hour.
                  </p>
                  <YesNo
                    value={wantsExtra}
                    onChange={setWantsExtra}
                    yesLabel="Yes, I will work extra"
                    label="Working extra hours"
                  />

                  {wantsExtra ? (
                    <div className="timeoff-planner-extras timeoff-planner-reveal">
                      {extraPeriods.map((period) => {
                        const canBank = period.bankDaysLeft > 0;
                        return (
                          <div key={period.id} className="timeoff-planner-extra-row">
                            <div className="timeoff-planner-extra-row__text">
                              <div className="timeoff-planner-extra-row__title">Extra hours during {period.label}</div>
                              <div className="timeoff-planner-extra-row__hint">
                                {canBank
                                  ? `Up to ${hrs(period.afterReduction)} helps here · ${plural(period.bankDaysLeft, 'workday')} left in this pay period to work them`
                                  : 'No workdays left in this pay period outside your time off'}
                              </div>
                              {period.extraOver > 0 ? (
                                <div className="timeoff-planner-warning" role="alert">
                                  <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                                  Only {hrs(period.afterReduction)} of extra time helps in this pay period - that is all
                                  the PATO it needs.
                                </div>
                              ) : null}
                            </div>
                            <InputNumber
                              min={0}
                              step={0.25}
                              value={extraByPeriod[period.id] ?? null}
                              onChange={(value) => setExtraByPeriod((prev) => ({ ...prev, [period.id]: typedNumber(value) }))}
                              onBlur={() => setExtraByPeriod((prev) => ({ ...prev, [period.id]: tidyNumber(prev[period.id]) }))}
                              disabled={!canBank}
                              placeholder="0"
                              aria-label={`Extra hours during ${period.label}`}
                              style={{ width: 110 }}
                            />
                          </div>
                        );
                      })}
                      <p className="timeoff-planner-sub">
                        Extra hours only count in the pay period you work them in.
                      </p>
                    </div>
                  ) : null}

                  {/* Unpaid: for somebody who would rather not spend PATO. It
                      is Entered time like reduction, but nobody pays it. */}
                  <h2 className="timeoff-planner-ask timeoff-planner-ask--section">
                    Would you like to take any of it unpaid?
                  </h2>
                  <p className="timeoff-planner-sub">
                    Unpaid time is logged as Entered. It is not paid, and it does not come out of your PATO balance.
                  </p>
                  <YesNo
                    value={wantsUnpaid}
                    onChange={(next) => {
                      setWantsUnpaid(next);
                      setUnpaidCapped(false);
                    }}
                    yesLabel="Yes, take some unpaid"
                    label="Taking time unpaid"
                  />

                  {wantsUnpaid ? (
                    <div className="timeoff-planner-extras timeoff-planner-reveal">
                      <div className="timeoff-planner-extra-row">
                        <div className="timeoff-planner-extra-row__text">
                          <div className="timeoff-planner-extra-row__title">Unpaid hours</div>
                          <div className="timeoff-planner-extra-row__hint">
                            {plan.beforeUnpaid > 0
                              ? `Up to ${hrs(plan.beforeUnpaid)} - the PATO left after reduction${plan.extraHours > 0 ? ' and extra hours' : ''}`
                              : 'Your extra hours already cover it - there is nothing left to take unpaid'}
                          </div>
                          {plan.unpaidOver > 0 || unpaidCapped ? (
                            <div className="timeoff-planner-warning" role="alert">
                              <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                              Only {hrs(plan.beforeUnpaid)} is left to cover, so you cannot take more than that unpaid.
                            </div>
                          ) : null}
                        </div>
                        <InputNumber
                          min={0}
                          step={0.25}
                          value={unpaidInput}
                          onChange={(value) => {
                            setUnpaidCapped(false);
                            setUnpaidInput(typedNumber(value));
                          }}
                          // Never more than the PATO it replaces: cut back, and
                          // say so.
                          onBlur={() => {
                            const typed = tidyNumber(unpaidInput);
                            if (typed != null && typed > plan.beforeUnpaid) {
                              setUnpaidInput(plan.beforeUnpaid);
                              setUnpaidCapped(true);
                            } else {
                              setUnpaidInput(typed);
                            }
                          }}
                          disabled={plan.beforeUnpaid === 0 && !(Number(unpaidInput) > 0)}
                          placeholder="0"
                          aria-label="Unpaid hours"
                          style={{ width: 110 }}
                        />
                      </div>
                    </div>
                  ) : null}
                </>
              )}

              <div className="timeoff-planner-actions">
                <Button appearance="ghost" size="lg" disabled={editsLocked} onClick={() => goBack('plan', 0)}>
                  Back
                </Button>
                <Button
                  appearance="primary"
                  size="lg"
                  disabled={plan.afterReduction > 0 && (wantsExtra == null || wantsUnpaid == null)}
                  onClick={() => advance('plan', 2)}
                >
                  Confirm
                </Button>
              </div>
            </div>
          ) : planReached >= 1 ? (
            <ConfirmedLine
              key="extra-done"
              disabled={editsLocked}
              ahead={planStage < 1}
              label="Extra and unpaid"
              value={[
                plan.extraHours > 0 ? `${hrs(plan.extraHours)} extra before you go` : 'No extra hours',
                plan.unpaidHours > 0 ? `${hrs(plan.unpaidHours)} unpaid` : 'none unpaid',
              ].join(' · ')}
              onChange={() => goBack('plan', 1)}
            />
          ) : null}

          {/* 3c. The sum: what is left for PATO. */}
          {planStage === 2 && isRevealing('plan', 2) ? (
            <WorkingCard key="pato-working" cardRef={activeStageRef} message={revealMessage} />
          ) : planStage === 2 ? (
            <div key="pato" ref={activeStageRef} className="timeoff-planner-card timeoff-planner-reveal">
              <div className="timeoff-planner-equation">
                <Figure value={formatHours(plan.totalHours)} label="hrs time off" />
                <Op>-</Op>
                <Figure value={formatHours(plan.reductionChosen)} label="hrs reduction" tone="good" />
                <Op>-</Op>
                <Figure value={formatHours(plan.extraHours)} label="hrs extra worked" tone="good" />
                {plan.unpaidHours > 0 ? (
                  <>
                    <Op>-</Op>
                    <Figure value={formatHours(plan.unpaidHours)} label="hrs unpaid" />
                  </>
                ) : null}
                <Op>=</Op>
                <Figure
                  value={formatHours(plan.patoHours)}
                  label="hrs PATO needed"
                  tone={plan.patoHours > 0 ? 'warn' : 'good'}
                />
              </div>

              {PAYROLL_ENABLED ? (
                <div className="timeoff-planner-balance">
                  <div className="timeoff-planner-balance__label"><PatoTerm /> balance after this ({tripYear})</div>
                  <div className={`timeoff-planner-balance__value bdc-num${patoAfter != null && patoAfter < 0 ? ' bdc-text-bad' : ''}`}>
                    {patoUnlimited ? 'Unlimited' : patoAfter == null ? '—' : hrs(patoAfter)}
                  </div>
                  <div className="timeoff-planner-balance__foot">
                    {patoUnlimited ? (
                      'Unlimited time off under the company policy'
                    ) : patoRemaining == null ? (
                      patoQuery.isError ? 'Balance unavailable right now' : 'Your balance could not be read from TimeCamp'
                    ) : (
                      <>
                        {hrs(patoRemaining)} remaining
                        {patoRequestedAhead > 0 ? `, less ${hrs(patoRequestedAhead)} already requested ahead` : ''}
                        {countOtherPlans ? `, less ${hrs(otherPlans.hours)} in your other plans` : ''}
                        , less {hrs(patoInTripYear)} for this trip in {tripYear}
                        {patoAfter < 0 ? ' · over your balance' : ''}
                      </>
                    )}
                  </div>

                  {/* The what-if: this trip on top of every other plan still
                      ahead, or on its own. */}
                  {!patoUnlimited && patoRemaining != null && otherPlans.ahead.length > 0 ? (
                    <div className="timeoff-planner-balance__scenario">
                      <Checkbox
                        checked={includeOtherPlans}
                        onChange={(_value, checked) => setIncludeOtherPlans(Boolean(checked))}
                      >
                        Include my other plans ({otherPlans.ahead.length})
                      </Checkbox>
                      {includeOtherPlans ? (
                        <ul className="timeoff-planner-balance__plans">
                          {otherPlans.ahead.map(({ row, hours }) => (
                            <li key={row.id}>
                              <span>
                                {row.name}
                                <span className="timeoff-planner-balance__when"> · {formatTripRange(row.start_date, row.end_date)}</span>
                              </span>
                              <span className="bdc-num">- {hrs(hours)}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  ) : null}
                  {tripCrossesYear ? (
                    <div className="timeoff-pato-caveat">
                      This trip runs into the next year, when the balance starts over. The figure is for {tripYear}.
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="timeoff-planner-actions">
                <Button appearance="ghost" size="lg" disabled={editsLocked} onClick={() => goBack('plan', 1)}>
                  Back
                </Button>
                <Button appearance="primary" size="lg" onClick={() => advance('plan', 3)}>
                  Confirm
                </Button>
              </div>
            </div>
          ) : planReached >= 2 ? (
            <ConfirmedLine
              key="pato-done"
              disabled={editsLocked}
              ahead={planStage < 2}
              label="PATO needed"
              value={hrs(plan.patoHours)}
              onChange={() => goBack('plan', 2)}
            />
          ) : null}

          {/* 3d. The entries to make. Every day away goes on one - knowing who
              is out matters even when it costs no PATO. */}
          {planStage === 3 && isRevealing('plan', 3) ? (
            <WorkingCard key="entries-working" cardRef={activeStageRef} message={revealMessage} />
          ) : planStage === 3 ? (
            <div key="entries" ref={activeStageRef} className="timeoff-planner-card timeoff-planner-reveal">
              <h2 className="timeoff-planner-ask">Your time off entries</h2>

              <div className="timeoff-planner-callout">
                <i className="bi bi-calendar-check" aria-hidden="true" />
                <div>
                  <strong>Log every day you will be away</strong>, even the days that cost no PATO - it is how
                  everyone knows when you are out. You can always mark more time as Entered; it is for tracking.
                </div>
              </div>

              <div className="timeoff-planner-entries">
                {enteredRuns.length > 0 ? (
                  <div className="timeoff-planner-entries__group">
                    <div className="timeoff-planner-entries__head">
                      <span className="timeoff-planner-kind timeoff-planner-kind--entered">Entered</span>
                      <span className="timeoff-planner-entries__how">Leave <strong>Needs review</strong> unticked</span>
                      <span className="timeoff-planner-entries__total bdc-num">{hrs(enteredRunHours)}</span>
                    </div>
                    <ul className="timeoff-planner-runs">
                      {enteredRuns.map((run) => (
                        <li key={`e-${run.start}`}>
                          <span>
                            {formatRun(run)}
                            <span className="timeoff-planner-runs__days"> · {plural(run.days, 'day')}</span>
                          </span>
                          <span className="bdc-num">{hrs(run.hours)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="timeoff-planner-entries__why">
                      {(() => {
                        const sources = [
                          plan.reductionChosen > 0 ? 'your reduction' : null,
                          plan.extraHours > 0 ? 'the extra hours you plan to work' : null,
                          plan.unpaidHours > 0 ? 'unpaid time' : null,
                        ].filter(Boolean);
                        if (sources.length === 0) return 'No PATO used.';
                        const list = sources.length === 1
                          ? sources[0]
                          : `${sources.slice(0, -1).join(', ')} and ${sources[sources.length - 1]}`;
                        return `Covered by ${list} - no PATO used.`;
                      })()}
                    </div>
                  </div>
                ) : null}

                {patoRuns.length > 0 ? (
                  <div className="timeoff-planner-entries__group">
                    <div className="timeoff-planner-entries__head">
                      <span className="timeoff-planner-kind timeoff-planner-kind--pato">Needs review</span>
                      <span className="timeoff-planner-entries__how">Tick <strong>Needs review</strong> - paid from <PatoTerm /></span>
                      <span className="timeoff-planner-entries__total bdc-num">{hrs(plan.patoHours)}</span>
                    </div>
                    <ul className="timeoff-planner-runs">
                      {patoRuns.map((run) => (
                        <li key={`p-${run.start}`}>
                          <span>
                            {formatRun(run)}
                            <span className="timeoff-planner-runs__days"> · {plural(run.days, 'day')}</span>
                            {splitDaysIn(run).map((day) => (
                              <span key={day.date} className="timeoff-planner-runs__note">
                                {formatShortDate(day.date)} is {hrs(day.enteredHours)} reduction and {hrs(day.patoHours)}{' '}
                                PATO. A day can only be on one entry, so it goes here with just its PATO hours.
                              </span>
                            ))}
                          </span>
                          <span className="bdc-num">{hrs(run.hours)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              {plan.reductionUnused > 0 && plan.patoHours > 0 ? (
                <div className="timeoff-planner-callout timeoff-planner-callout--info">
                  <i className="bi bi-info-circle-fill" aria-hidden="true" />
                  <div>
                    You still have {hrs(plan.reductionUnused)} of reduction for these dates. You do not need PATO for
                    those hours - they could be Entered instead.{' '}
                    <button type="button" className="bdc-linkbtn" onClick={() => setReductionInput(null)}>
                      Use all my reduction
                    </button>
                  </div>
                </div>
              ) : null}

              {plan.unpaidHours > 0 ? (
                <div className="timeoff-planner-callout timeoff-planner-callout--info">
                  <i className="bi bi-cash-coin" aria-hidden="true" />
                  <div>
                    {hrs(plan.unpaidHours)} of your Entered time is unpaid
                    {plan.periods.filter((period) => period.unpaidUsed > 0).length > 1
                      ? ` (${plan.periods.filter((period) => period.unpaidUsed > 0)
                        .map((period) => `${hrs(period.unpaidUsed)} in ${period.label}`).join(', ')})`
                      : ''}
                    . It will not be paid, and it leaves your PATO balance alone.
                  </div>
                </div>
              ) : null}

              {plan.extraHours > 0 ? (
                <div className="timeoff-planner-callout timeoff-planner-callout--info">
                  <i className="bi bi-clock-history" aria-hidden="true" />
                  <div>
                    This plan counts on you working{' '}
                    {plan.periods.filter((period) => period.extraUsed > 0)
                      .map((period) => `${hrs(period.extraUsed)} extra during ${period.label}`)
                      .join(' and ')}
                    . Any of those extra hours you do not work will be unpaid.
                  </div>
                </div>
              ) : null}

              {/* Saving: the answers, so the plan can be reopened and worked
                  out again later. Nothing is submitted. */}
              <div className="timeoff-planner-save">
                <label className="timeoff-planner-save__label" htmlFor="planner-plan-name">
                  {openPlan ? 'Saved plan' : 'Save this plan to come back to it'}
                </label>
                <div className="timeoff-planner-save__row">
                  <input
                    id="planner-plan-name"
                    className="rs-input"
                    maxLength={120}
                    value={planName ?? openPlan?.name ?? defaultPlanName}
                    onChange={(event) => setPlanName(event.target.value)}
                    placeholder={defaultPlanName}
                  />
                  <Button
                    appearance="primary"
                    loading={savePlanMutation.isPending}
                    disabled={Boolean(openPlan) && !planDirty}
                    onClick={() => savePlan()}
                  >
                    <i className="bi bi-bookmark" aria-hidden="true" /> {openPlan ? 'Save changes' : 'Save plan'}
                  </Button>
                  {openPlan ? (
                    <Button appearance="ghost" disabled={savePlanMutation.isPending} onClick={() => savePlan({ asNew: true })}>
                      Save as new
                    </Button>
                  ) : null}
                </div>
                <div className="timeoff-planner-save__hint">
                  {openPlan
                    ? (planDirty ? 'You have unsaved changes.' : `Saved ${formatSavedAt(openPlan.updatedAt)}. Figures are worked out fresh each time you open it.`)
                    : 'Only you can see your saved plans. Saving does not submit anything.'}
                </div>
              </div>

              <div className="timeoff-planner-actions">
                <Button appearance="ghost" size="lg" onClick={() => goBack('plan', 2)}>
                  Back
                </Button>
                <Button
                  appearance="subtle"
                  size="lg"
                  onClick={openPlan ? () => navigate('/time-off/planner') : startOver}
                >
                  {openPlan ? 'Close plan' : 'Start over'}
                </Button>
                <Button
                  appearance="primary"
                  size="lg"
                  disabled={enteredRuns.length + patoRuns.length === 0 && unrecordedPlanRequestIds.length === 0}
                  onClick={previewRequests}
                >
                  Preview requests
                </Button>
              </div>
            </div>
          ) : planReached >= 3 ? (
            <ConfirmedLine
              key="entries-done"
              disabled={editsLocked}
              ahead
              label="Your entries"
              value={[
                enteredRuns.length > 0 ? `${enteredRuns.length} Entered` : null,
                patoRuns.length > 0 ? `${patoRuns.length} Needs review` : null,
              ].filter(Boolean).join(' · ') || 'None'}
              onChange={() => goBack('plan', 3)}
            />
          ) : null}
        </div>
      ) : null}

      {step === 3 && stepWorking(3) ? (
        <div className="timeoff-planner-details">
          <WorkingCard key="step-working-3" cardRef={activeStageRef} message={revealMessage} />
        </div>
      ) : null}

      {step === 3 && submission && !stepWorking(3) ? (
        /* Submit: the plan's requests, the ones that need no review beside the
           ones that do, each side with its own comment and its own submit. */
        <div className="timeoff-planner-submit timeoff-planner-reveal">
          <div className="timeoff-planner-submit__intro">
            <h2 className="timeoff-planner-ask">Review and submit your requests</h2>
            <p className="timeoff-planner-sub">
              {formatTripRange(submission.plan.start, submission.plan.end)} · {hrs(submission.plan.totalHours)} of time
              off as {plural(submission.items.length, 'request')}. Every day you will be away is on one of them.
              {openPlan ? '' : ' Submitting also saves this plan to your plans, so you can see what became of it.'}
            </p>
          </div>

          {previewStale ? (
            <Message type="warning" showIcon>
              Your plan changed after this preview. Review a fresh preview before submitting.
              {' '}
              <Button appearance="link" disabled={editsLocked} onClick={previewRequests}>
                Preview updated requests
              </Button>
            </Message>
          ) : null}

          {allSubmitted ? (
            <div className="timeoff-planner-callout timeoff-planner-submit__done">
              <i className="bi bi-check-circle-fill" aria-hidden="true" />
              <div>
                <strong>All of your requests are in.</strong> Requests that need review have gone to your approver;
                the rest are recorded already.{' '}
                <button type="button" className="bdc-linkbtn" onClick={() => navigate('/time-off/requests')}>
                  View my requests
                </button>
              </div>
            </div>
          ) : null}

          <div className={`timeoff-planner-submit__columns${itemsOf('entered').length === 0 || itemsOf('pato').length === 0 ? ' timeoff-planner-submit__columns--single' : ''}`}>
            {['entered', 'pato'].filter((kind) => itemsOf(kind).length > 0).map((kind) => {
              const meta = SUBMIT_KINDS[kind];
              const items = itemsOf(kind);
              const remaining = remainingOf(kind);
              const total = items.reduce((sum, item) => sum + item.hours, 0);
              const comment = submitComments[kind];
              const locked = remaining.length === 0 || Boolean(submitting);
              return (
                <section key={kind} className={`timeoff-planner-submit__col timeoff-planner-submit__col--${kind}`}>
                  <header className="timeoff-planner-submit__head">
                    <span className={`timeoff-planner-kind timeoff-planner-kind--${kind === 'pato' ? 'pato' : 'entered'}`}>{meta.chip}</span>
                    <span className="timeoff-planner-submit__total bdc-num">{hrs(total)}</span>
                  </header>
                  <div className="timeoff-planner-submit__title">{meta.title}</div>
                  <div className="timeoff-planner-submit__how">{meta.how}</div>

                  <ul className="timeoff-planner-submit__list">
                    {items.map((item) => {
                      const status = submitStatus[item.key] || { state: 'ready' };
                      return (
                        <li key={item.key} className={`timeoff-planner-request timeoff-planner-request--${status.state}`}>
                          <div className="timeoff-planner-request__main">
                            <div className="timeoff-planner-request__dates">{formatRun(item)}</div>
                            <div className="timeoff-planner-request__meta">
                              Time off · {plural(item.days, 'day')} · {meta.needsApproval ? 'Needs review ticked' : 'Needs review unticked'}
                            </div>
                            {item.splitDays.map((day) => (
                              <div key={day.date} className="timeoff-planner-request__note">
                                {formatShortDate(day.date)} is {hrs(day.enteredHours)} reduction and {hrs(day.patoHours)} PATO - only
                                the PATO hours are on this request.
                              </div>
                            ))}
                            {status.state === 'error' ? (
                              <div className="timeoff-planner-warning" role="alert">
                                <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                                {status.message}
                              </div>
                            ) : null}
                          </div>
                          <div className="timeoff-planner-request__side">
                            <span className="timeoff-planner-request__hours bdc-num">{hrs(item.hours)}</span>
                            <span className="timeoff-planner-request__status">
                              {status.state === 'submitting' ? (
                                <><span className="timeoff-planner-spinner timeoff-planner-spinner--sm" aria-hidden="true" /> Submitting</>
                              ) : status.state === 'done' ? (
                                <><i className="bi bi-check-circle-fill" aria-hidden="true" /> {meta.needsApproval ? 'Sent for review' : 'Recorded'}</>
                              ) : status.state === 'error' ? (
                                <><i className="bi bi-x-circle-fill" aria-hidden="true" /> Not submitted</>
                              ) : (
                                'Ready'
                              )}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  <label className="timeoff-planner-submit__label" htmlFor={`submit-comment-${kind}`}>Comment *</label>
                  <textarea
                    id={`submit-comment-${kind}`}
                    className="rs-input"
                    rows={3}
                    value={comment}
                    disabled={locked}
                    onChange={(event) => setSubmitComments((prev) => ({ ...prev, [kind]: event.target.value }))}
                  />
                  <Checkbox
                    checked={submitCalendar[kind]}
                    disabled={locked}
                    onChange={(_value, checked) => setSubmitCalendar((prev) => ({ ...prev, [kind]: Boolean(checked) }))}
                  >
                    Add to company calendar?
                  </Checkbox>

                  <Button
                    appearance="primary"
                    block
                    loading={submitting === kind || (submitting === 'all' && remaining.some((item) => submitStatus[item.key]?.state === 'submitting'))}
                    disabled={locked || previewStale || !comment.trim()}
                    onClick={() => submitKind(kind)}
                  >
                    {remaining.length === 0
                      ? (meta.needsApproval ? 'Sent for review' : 'Recorded')
                      : `${meta.needsApproval ? 'Submit for review' : 'Submit'} - ${plural(remaining.length, 'request')}`}
                  </Button>
                </section>
              );
            })}
          </div>

          <div className="timeoff-planner-actions">
            <Button appearance="ghost" size="lg" disabled={editsLocked} onClick={() => goBack('step', 2)}>
              Back to plan
            </Button>
            {submission.createdIds?.length > 0 ? (
              <Button
                appearance="ghost"
                size="lg"
                loading={submitting === 'recording'}
                disabled={Boolean(submitting) || previewStale}
                onClick={retrySubmissionRecording}
              >
                Retry linking submitted requests
              </Button>
            ) : null}
            {allSubmitted ? (
              <Button appearance="primary" size="lg" onClick={() => navigate('/time-off/requests')}>
                View my requests
              </Button>
            ) : itemsOf('entered').length > 0 && itemsOf('pato').length > 0 ? (
              <Button
                appearance="primary"
                size="lg"
                loading={submitting === 'all'}
                disabled={Boolean(submitting) || previewStale || ['entered', 'pato'].some((kind) => remainingOf(kind).length > 0 && !submitComments[kind].trim())}
                onClick={() => submitKind('all')}
              >
                {anySubmitted ? 'Submit the rest' : 'Submit all requests'}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

    </div>
  );
}
